import React, { FC, HTMLInputTypeAttribute, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import { InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InputRegexPattern, InputTextFormat } from '../../../models/Common';
import { getInputFormat } from './inputFormats';

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

interface FormInputTextProps {
  name: string;
  label: string;
  className: string;
  isRequired?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  format?: InputTextFormat | null;
  minValue?: number | string;
  maxValue?: number | string;
  endAdornment?: string;
  helperText?: string;
  type?: HTMLInputTypeAttribute;
  autoComplete?: string;
  placeholder?: string;
  fieldHasName?: boolean;
  maxValueErrorMessage?: string;
  deps?: string[];
  validate?: (value: string) => boolean | string;
}

const defaultProps = {
  isRequired: false,
  minLength: 0,
  maxLength: 99,
  pattern: InputRegexPattern.Default,
  format: null,
  minValue: 0,
  maxValue: Infinity,
  endAdornment: '',
  helperText: undefined,
  type: 'text',
  autoComplete: 'off',
  placeholder: '',
  fieldHasName: false,
  maxValueErrorMessage: '',
};

export const FormInputText: FC<FormInputTextProps> = ({
  name,
  label,
  className,
  isRequired,
  minLength,
  maxLength,
  minValue,
  maxValue,
  pattern,
  format,
  endAdornment,
  helperText,
  type,
  deps,
  validate,
  autoComplete = 'off',
  placeholder,
  fieldHasName,
  maxValueErrorMessage,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { control } = useFormContext();
  const { t } = useTranslation();
  const validationRules = {
    required: {
      value: isRequired || defaultProps.isRequired,
      message: t('validation.thisFieldIsRequired'),
    },
    minLength: {
      value: minLength || defaultProps.minLength,
      message: `${t('validation.theNumberOfCharactersMustBeAtLeast')} ${minLength}`,
    },
    maxLength: {
      value: maxLength || defaultProps.maxLength,
      message: `${t('validation.theNumberOfCharactersMustBeNoMoreThan')} ${maxLength}`,
    },
    pattern: {
      value: pattern || defaultProps.pattern,
      message: t('validation.invalidValue'),
    },
    min: {
      value: minValue || defaultProps.minValue,
      message: `${t('validation.minimumAmountIs', { val: minValue })} ${t('common.uah')}`,
    },
    max: {
      value: maxValue || defaultProps.maxValue,
      message: maxValueErrorMessage || `${t('validation.maximumAmountIs', { val: maxValue })} ${t('common.uah')}`,
    },
    validate,
    deps,
  };

  const NumberFormatCustom = React.forwardRef<HTMLElement, CustomProps>((numberFormatProps, ref) => {
    const { onChange, ...other } = numberFormatProps;
    const inputFormat = getInputFormat(format || null);
    return (
      <NumberFormat
        {...other}
        onValueChange={(values) => {
          onChange({
            target: {
              name: numberFormatProps.name,
              value: values.value,
            },
          });
        }}
        {...inputFormat}
        getInputRef={ref}
      />
    );
  });

  const getStartAdornment = (inputFormat: InputTextFormat | null | undefined) => {
    switch (inputFormat) {
      case InputTextFormat.Phone:
        return (
          <InputAdornment className="phone-code" position="start">
            +38
          </InputAdornment>
        );
      case InputTextFormat.Iban:
        return (
          <InputAdornment className="iban-code" position="start">
            UA
          </InputAdornment>
        );
      default:
        return undefined;
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validationRules}
      render={({ field: { onChange, value }, fieldState: { error, isDirty, isTouched } }) => (
        <TextField
          type={type}
          error={!!error}
          helperText={(error && error.message) || (helperText && helperText)}
          label={label}
          onChange={onChange}
          name={fieldHasName ? name : undefined}
          value={value || ''}
          className={`input input__text-field__${className}`}
          {...props}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          InputProps={{
            inputComponent: format ? useMemo(() => NumberFormatCustom as never, []) : undefined,
            endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
            startAdornment: (isFocused || value) && getStartAdornment(format),
            autoComplete,
          }}
          InputLabelProps={{ shrink: !!value || !!placeholder || isFocused || isDirty || isTouched }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

FormInputText.defaultProps = defaultProps;
